// Adapter layer for session-lifecycle endpoints (login, logout).
//
// Contract reference: docs/api-contracts/login.md (FE proposal, awaiting BE sign-off).
//
// Slice 2 alignment: login() lives here alongside logout(); both branches
// (mock and real HTTP) sit behind VITE_USE_MOCK. The mock retains the
// behavior the page expects (5-fail lockout, `demo1234` success password,
// `inactive` substring → INACTIVE_ACCOUNT, role inferred from `nurse`
// substring). When BE is ready, set VITE_USE_MOCK=false and the real branch
// takes over with no consumer-side changes.

import { signInWithCustomToken, signOut } from 'firebase/auth';

import { httpClient } from '@/api/http-client';
import { auth } from '@/api/firebase';
import { endpoints } from '@/api/endpoints';
import { ApiError, mapAxiosError } from '@/api/error';
import { mockWithdrawnAccounts } from '@/api/adapters/my-profile';
import { clearSessionHint, hasSessionHint, useAuthStore } from '@/app/store/auth.store';
import type { AccountStatus, PortalRole } from '@/shared/types/auth';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface AuthUser {
  email: string;
  name: string;
  role: PortalRole;
  department: string;
  status: AccountStatus;
}

export interface LoginResponse {
  accessToken: string;
  user: AuthUser;
}

interface ApiSuccess<T> {
  data: T;
}

const IS_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

const MOCK_LATENCY_MS = 200;
const MOCK_LOGIN_LATENCY_MS = 600;
// Must satisfy the PRD §4.1 password policy (8+ chars, letter+digit+symbol)
// so the login form's Zod schema accepts it. See features/auth/schemas/login.schema.ts.
const MOCK_SUCCESS_PASSWORD = 'demo1234!';
const MOCK_LOCKOUT_THRESHOLD = 5;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// Mock-side state: in-memory failure counter per email. Lives for the
// page-load lifetime. BE owns the real counter — this Map is deleted when
// VITE_USE_MOCK=false.
const mockFailureCounts = new Map<string, number>();

/**
 * Clear the in-memory failed-login counter for an email (mock-only).
 * Called by the password-reset adapter after a successful password change
 * so the account is automatically unlocked. No-op when VITE_USE_MOCK=false.
 */
export function clearLoginFailureCounter(email: string): void {
  if (IS_MOCK) {
    mockFailureCounts.delete(email.toLowerCase());
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /medical-staff/auth/login
// ──────────────────────────────────────────────────────────────────────────────
export async function login(payload: LoginPayload): Promise<LoginResponse> {
  if (IS_MOCK) {
    await delay(MOCK_LOGIN_LATENCY_MS);
    const { email, password } = payload;

    // The mock carries only the wire code (never a hardcoded Korean message): the page maps a code → its
    // Korean label via t(), exactly as it does for the real BE response. This keeps the FE Korean-free.
    if (mockWithdrawnAccounts.has(email)) {
      throw new ApiError('ACCOUNT_WITHDRAWN', 'ACCOUNT_WITHDRAWN');
    }

    if (email.toLowerCase().includes('inactive')) {
      throw new ApiError('INACTIVE_ACCOUNT', 'INACTIVE_ACCOUNT');
    }

    if (password !== MOCK_SUCCESS_PASSWORD) {
      const count = (mockFailureCounts.get(email) ?? 0) + 1;
      mockFailureCounts.set(email, count);

      if (count >= MOCK_LOCKOUT_THRESHOLD) {
        throw new ApiError('ACCOUNT_LOCKED', 'ACCOUNT_LOCKED', undefined, {
          failureCount: count,
        });
      }
      throw new ApiError('INVALID_CREDENTIALS', 'INVALID_CREDENTIALS', undefined, {
        failureCount: count,
      });
    }

    mockFailureCounts.delete(email);
    const role: PortalRole = email.toLowerCase().includes('nurse') ? 'NURSE' : 'DOCTOR';
    return {
      accessToken: `mock-jwt-${role.toLowerCase()}-${Date.now()}`,
      user: {
        email,
        name: role === 'DOCTOR' ? '홍길동' : '이간호',
        role,
        department: '정신건강의학과',
        status: 'ACTIVE',
      },
    };
  }
  try {
    const { data } = await httpClient.post<ApiSuccess<LoginResponse>>(
      endpoints.medicalStaff.login,
      payload,
    );
    const result = data.data;
    // Backend-mediated login (mirror admin-web): the BE returns a Firebase CUSTOM token. Exchange it via
    // signInWithCustomToken so the Firebase SDK holds the (auto-refreshing) ID token the request interceptor
    // sends as Bearer; the single-use custom token is then discarded. Done before the page navigates so the
    // first authenticated call already carries a valid token.
    await signInWithCustomToken(auth, result.accessToken);
    // CRC is a first-class FE role now: same data scope as NURSE, but no write permissions.
    const role = result.user.role as PortalRole;
    return { ...result, user: { ...result.user, role } };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /medical-staff/auth/logout
// ──────────────────────────────────────────────────────────────────────────────

// Why the session ended, sent to the BE so its audit row records the right code (GATE-11 session split):
// SESSION_TIMEOUT → LOGOUT_SESSION_TIMEOUT, everything else → the default LOGOUT_BY_USER.
export type LogoutReason = 'USER' | 'SESSION_TIMEOUT';

// Pass `{ reason: 'SESSION_TIMEOUT' }` from the 60-minute idle timer so the server records a session-timeout
// logout rather than an ordinary user logout; omitting it defaults to USER (the button-click + 401-interceptor
// call sites stay a plain call). Mirrors admin-web's logoutSession.
export async function logout(options?: { reason?: LogoutReason }): Promise<{ ok: true }> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    return { ok: true };
  }
  // Read before any await: an EMR session (SHCT-994) has no refresh cookie, so a cookie riding on its logout
  // would be another staff member's on this browser — and the server would end that person's session.
  const isEmrSession = useAuthStore.getState().emrPatientUid !== null;
  try {
    // Revoke the server-side refresh token + clear its cookie. The cookie rides via withCredentials; the
    // custom CSRF header is required by the BE (a cross-site form can't set it). BE returns 204 (no body).
    // Only append the reason for a non-default value, so an ordinary user logout stays a plain call.
    const url =
      options?.reason && options.reason !== 'USER'
        ? `${endpoints.medicalStaff.logout}?reason=${options.reason}`
        : endpoints.medicalStaff.logout;
    await httpClient.post(url, null, {
      headers: { 'X-Refresh-Auth': '1' },
      ...(isEmrSession && { withCredentials: false }),
    });
    return { ok: true };
  } catch (err) {
    throw mapAxiosError(err);
  } finally {
    // Drop the Firebase session regardless (best-effort) so the in-memory ID token is gone.
    void signOut(auth);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Reload session re-establishment (FEATURE_005) — POST /api/v1/medical/auth/refresh
// ──────────────────────────────────────────────────────────────────────────────
/**
 * Silently re-establish the session on a hard reload (the ID token lives in memory only, so it's gone).
 * POSTs /auth/refresh with the HttpOnly refresh cookie (withCredentials) + the CSRF header; the BE rotates
 * the cookie and returns a fresh Firebase custom token, which we exchange via signInWithCustomToken so the
 * interceptor's Bearer works on the next call. Returns the staff user on success, or null (not signed in)
 * on any failure — a missing / expired / superseded cookie (401) or an unreachable BE. No-op in mock dev.
 */
export async function refreshSession(): Promise<AuthUser | null> {
  if (IS_MOCK) {
    return null;
  }
  // No session was ever established on this device → nothing to restore. Skipping
  // the call avoids a benign but noisy 401 in the console on the login screen.
  if (!hasSessionHint()) {
    return null;
  }
  try {
    const { data } = await httpClient.post<ApiSuccess<LoginResponse>>(
      endpoints.medicalStaff.refresh,
      null,
      { headers: { 'X-Refresh-Auth': '1' } },
    );
    const result = data.data;
    await signInWithCustomToken(auth, result.accessToken);
    const role = result.user.role as PortalRole;
    return { ...result.user, role };
  } catch (err) {
    // A definitive 401 means the refresh cookie is gone/expired — clear the stale
    // hint so subsequent reloads don't retry (and re-log the 401). Keep the hint on
    // transient/network errors so a valid session can still recover on next reload.
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 401) {
      clearSessionHint();
    }
    return null;
  }
}

let bootPromise: Promise<AuthUser | null> | null = null;

/**
 * Run the reload re-establish exactly ONCE per page load. The refresh token is single-use (rotated on every
 * call), so React StrictMode's double-effect must not fire two /auth/refresh calls — the second would find
 * the token already rotated and 401. Memoizing the promise makes both effects await the same single call.
 */
export function bootstrapSession(): Promise<AuthUser | null> {
  if (!bootPromise) {
    bootPromise = refreshSession();
  }
  return bootPromise;
}
